import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Copy } from "lucide-react";

export const DIAS = [
  ["lun", "Lunes"], ["mar", "Martes"], ["mie", "Miércoles"], ["jue", "Jueves"],
  ["vie", "Viernes"], ["sab", "Sábado"], ["dom", "Domingo"],
];

const TURNO_DEFECTO = { inicio: "09:00", fin: "17:00" };
const TURNO_FLEXIBLE_DEFECTO = { inicio: "06:00", fin: "22:00" };

export const HORARIO_DEFECTO = Object.fromEntries(
  DIAS.map(([k], i) => [k, { activo: i < 5, turnos: [{ ...TURNO_DEFECTO }] }])
);

/** Ventana amplia Lun–Vie: permiten fichar cuando quieran y acumular horas. */
export const HORARIO_FLEXIBLE_DEFECTO = Object.fromEntries(
  DIAS.map(([k], i) => [k, { activo: i < 5, turnos: [{ ...TURNO_FLEXIBLE_DEFECTO }] }])
);

export function normalizarHorario(horario) {
  const base = { ...HORARIO_DEFECTO, ...horario };
  const out = {};
  for (const [k] of DIAS) {
    const d = base[k] || {};
    let turnos = d.turnos;
    if (!turnos?.length && d.inicio && d.fin) {
      turnos = [{ inicio: d.inicio, fin: d.fin }];
    }
    if (!turnos?.length) {
      turnos = [{ ...TURNO_DEFECTO }];
    }
    out[k] = { activo: !!d.activo, turnos: turnos.map((t) => ({ inicio: t.inicio, fin: t.fin })) };
  }
  return out;
}

export function horarioParaGuardar(horario) {
  const n = normalizarHorario(horario);
  const out = {};
  for (const [k] of DIAS) {
    out[k] = { activo: n[k].activo, turnos: n[k].turnos };
  }
  return out;
}

export function resumenHorario(horario) {
  const n = normalizarHorario(horario);
  const activos = DIAS.filter(([k]) => n[k]?.activo);
  if (activos.length === 0) return "Sin días activos";
  const ref = n[activos[0][0]];
  const tramos = ref.turnos.map((t) => `${t.inicio}–${t.fin}`).join(", ");
  const multi = ref.turnos.length > 1 ? ` (${ref.turnos.length} turnos/día)` : "";
  return `${activos[0][1].slice(0, 3)}–${activos[activos.length - 1][1].slice(0, 3)} · ${tramos}${multi}`;
}

export function HorarioEditor({ horario, onChange, prefix = "horario", plantillas = [], maxTurnosPorDia = null }) {
  const h = normalizarHorario(horario);

  const setDia = (k, patch) => onChange({ ...h, [k]: { ...h[k], ...patch } });

  const setTurno = (k, idx, campo, valor) => {
    const turnos = h[k].turnos.map((t, i) => (i === idx ? { ...t, [campo]: valor } : t));
    setDia(k, { turnos });
  };

  const addTurno = (k) => {
    setDia(k, { turnos: [...h[k].turnos, { inicio: "09:00", fin: "13:00" }] });
  };

  const removeTurno = (k, idx) => {
    if (h[k].turnos.length <= 1) return;
    setDia(k, { turnos: h[k].turnos.filter((_, i) => i !== idx) });
  };

  const aplicarPlantilla = (turnoId) => {
    const t = plantillas.find((p) => String(p.id) === turnoId);
    if (t?.horario) onChange(normalizarHorario(t.horario));
  };

  const copiarLunes = () => {
    const lun = h.lun;
    const out = { ...h };
    for (const [k] of DIAS) {
      if (k === "lun") continue;
      out[k] = {
        activo: lun.activo,
        turnos: lun.turnos.map((t) => ({ inicio: t.inicio, fin: t.fin })),
      };
    }
    onChange(out);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          onClick={copiarLunes}
          data-testid={`${prefix}-copiar-lunes`}
        >
          <Copy className="h-3.5 w-3.5" />
          Copiar Lunes al resto de días
        </Button>
      </div>
      {plantillas.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs text-zinc-500">Aplicar plantilla de turno</Label>
          <Select onValueChange={aplicarPlantilla}>
            <SelectTrigger data-testid={`${prefix}-plantilla-select`}>
              <SelectValue placeholder="Seleccionar turno como plantilla…" />
            </SelectTrigger>
            <SelectContent>
              {plantillas.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  <span className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                    {t.nombre}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-3">
        {DIAS.map(([k, label]) => (
          <div key={k} className="rounded-lg border border-zinc-100 px-3 py-2">
            <div className="flex items-center gap-3">
              <Switch
                checked={h[k]?.activo || false}
                onCheckedChange={(v) => setDia(k, { activo: v })}
                data-testid={`${prefix}-${k}-switch`}
              />
              <span className="w-20 text-sm font-medium">{label}</span>
              {h[k]?.activo && (maxTurnosPorDia === null || h[k].turnos.length < maxTurnosPorDia) && (
                <Button type="button" size="sm" variant="ghost" className="ml-auto h-7 gap-1 text-xs" onClick={() => addTurno(k)}>
                  <Plus className="h-3 w-3" />
                  Turno
                </Button>
              )}
            </div>
            {h[k]?.activo && (
              <div className="mt-2 space-y-2 pl-11">
                {h[k].turnos.map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      type="time"
                      className="h-8 w-28"
                      value={t.inicio}
                      onChange={(e) => setTurno(k, idx, "inicio", e.target.value)}
                    />
                    <span className="text-zinc-300">—</span>
                    <Input
                      type="time"
                      className="h-8 w-28"
                      value={t.fin}
                      onChange={(e) => setTurno(k, idx, "fin", e.target.value)}
                    />
                    {h[k].turnos.length > 1 && (
                      <button type="button" onClick={() => removeTurno(k, idx)} className="rounded p-1 text-zinc-400 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
